import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  BellRing,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Inbox,
  LoaderCircle,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { NotificationHistoryItem, NotificationHistoryFilters, NotificationHistoryPagination } from '../models/Notification';
import { formatDateString, formatTimeString, parseDateString } from '../lib/utils';
import { NotificationService } from '../services/notificationService';

const notificationService = new NotificationService();
const filterControlClassName = 'block h-11 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100';

export const Notifications: React.FC = observer(() => {
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationHistoryItem[]>([]);
  const [pagination, setPagination] = useState<NotificationHistoryPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NotificationHistoryFilters>({
    page: 1,
    limit: 15, // Max 15 per page
    type: undefined,
    contactNumber: undefined
  });

  // Fetch notifications when filters change
  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await notificationService.getNotificationHistory(filters);
        if (response.success && response.data) {
          setNotifications(response.data.notifications);
          setPagination(response.data.pagination);
        } else {
          setError('Failed to load notifications');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred while loading notifications');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [filters]);

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleTypeFilterChange = (type: string) => {
    setFilters(prev => ({ 
      ...prev, 
      type: type === 'all' ? undefined : type,
      page: 1 // Reset to first page when filter changes
    }));
  };

  const handleContactNumberChange = (contactNumber: string) => {
    setFilters(prev => ({ 
      ...prev, 
      contactNumber: contactNumber.trim() || undefined,
      page: 1 // Reset to first page when filter changes
    }));
  };
  const activeFilterCount = [Boolean(filters.type), Boolean(filters.contactNumber?.trim())].filter(Boolean).length;


  const formatTimestamp = (timestamp: string) => {
    // Use standardized formatting functions
    const date = parseDateString(timestamp);
    if (!date) {
      return (
        <div className="text-left">
          <div className="font-medium text-red-500">Invalid Date</div>
        </div>
      );
    }
    
    const formattedDate = formatDateString(date);
    const formattedTime = formatTimeString(date);
    
    return (
      <div className="text-left">
        <div className="font-medium text-slate-800">{formattedDate}</div>
        <div className="text-xs text-slate-500">{formattedTime}</div>
      </div>
    );
  };

  const formatNotificationType = (type: string) => {
    const typeConfig: Record<string, { label: string; bgColor: string; textColor: string }> = {
      'New Request': { label: 'New Request', bgColor: 'border-blue-200 bg-blue-50', textColor: 'text-blue-700' },
      'Request Accepted': { label: 'Request Accepted', bgColor: 'border-emerald-200 bg-emerald-50', textColor: 'text-emerald-700' },
      'Request Timeout': { label: 'Request Timeout', bgColor: 'border-red-200 bg-red-50', textColor: 'text-red-700' },
      'Request Completed': { label: 'Request Completed', bgColor: 'border-violet-200 bg-violet-50', textColor: 'text-violet-700' },
      'Payment Successful': { label: 'Payment Successful', bgColor: 'border-amber-200 bg-amber-50', textColor: 'text-amber-700' },
      'Payment Received': { label: 'Payment Received', bgColor: 'border-amber-200 bg-amber-50', textColor: 'text-amber-700' },
      'System Alert': { label: 'System Alert', bgColor: 'border-slate-200 bg-slate-50', textColor: 'text-slate-700' }
    };

    const config = typeConfig[type] || { label: type, bgColor: 'border-slate-200 bg-slate-50', textColor: 'text-slate-700' };
    return (
      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
        {config.label}
      </span>
    );
  };

  const formatRecipient = (notification: NotificationHistoryItem) => {
    const recipient = notification.recipient;
    if (!recipient || typeof recipient !== 'object') {
      return (
        <div className="text-left">
          <div className="font-medium text-slate-400">N/A</div>
        </div>
      );
    }
    
    return (
      <div className="text-left">
        {recipient.name && (
          <div className="font-medium text-slate-800">{recipient.name}</div>
        )}
        {recipient.contactNumber && (
          <div className="text-sm text-slate-500">{recipient.contactNumber}</div>
        )}
        {!recipient.name && !recipient.contactNumber && (
          <div className="font-medium text-slate-400">N/A</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-lg shadow-slate-950/10 sm:px-6">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" aria-hidden="true" />
            Communication activity
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Notifications</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Monitor service alerts, request updates, payment messages, and their recipients.</p>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="flex flex-col items-center">
            <LoaderCircle className="mb-3 h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-slate-900">Loading notifications…</h3>
            <p className="mt-1 text-sm text-slate-500">Fetching the latest communication history.</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm" role="alert">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
          <div>
            <h3 className="text-sm font-semibold text-red-900">Unable to load notifications</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Notifications Table */}
      {!loading && !error && (
        <div className="admin-table-shell">
          <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><BellRing className="h-[18px] w-[18px]" aria-hidden="true" /></span><div><h2 className="text-base font-semibold text-slate-950">Notification history</h2><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">{pagination ? `Showing ${pagination.totalCount === 0 ? 0 : ((pagination.currentPage - 1) * pagination.limit) + 1}–${Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of ${pagination.totalCount} notifications` : 'System messages delivered across the service network'}</p></div></div>
            <button type="button" onClick={() => setAreFiltersOpen(current => !current)} aria-expanded={areFiltersOpen} aria-controls="notification-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters{activeFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{activeFilterCount}</span>}<ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" /></button>
          </div>
          <div id="notification-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_9rem]">
              <div><label htmlFor="typeFilter" className="mb-1.5 block text-xs font-semibold text-slate-600">Notification type</label><select id="typeFilter" className={filterControlClassName} value={filters.type || 'all'} onChange={(event) => handleTypeFilterChange(event.target.value)}><option value="all">All types</option><option value="New Request">New Request</option><option value="Request Accepted">Request Accepted</option><option value="Request Timeout">Request Timeout</option><option value="Request Completed">Request Completed</option><option value="Payment Successful">Payment Successful</option></select></div>
              <div><label htmlFor="contactFilter" className="mb-1.5 block text-xs font-semibold text-slate-600">Contact number</label><div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" /><input id="contactFilter" type="search" inputMode="tel" placeholder="Search by contact number" className={`${filterControlClassName} pl-10`} value={filters.contactNumber || ''} onChange={(event) => handleContactNumberChange(event.target.value)} /></div></div>
              <div><label htmlFor="notification-items-per-page" className="mb-1.5 block text-xs font-semibold text-slate-600">Rows</label><select id="notification-items-per-page" className={filterControlClassName} value={filters.limit} onChange={(event) => setFilters(prev => ({ ...prev, limit: Number(event.target.value), page: 1 }))}><option value={10}>10 per page</option><option value={15}>15 per page</option><option value={25}>25 per page</option><option value={50}>50 per page</option></select></div>
            </div>
            {activeFilterCount > 0 && <div className="mt-4 flex justify-end"><button type="button" onClick={() => setFilters(prev => ({ ...prev, type: undefined, contactNumber: undefined, page: 1 }))} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button></div>}
          </div>
          <div className="admin-table-scroll" role="region" aria-label="Notifications data table" tabIndex={0}>
            <table className="admin-data-table min-w-max">
              <thead>
                <tr>
                  <th>Serial</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Recipient</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {notifications.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-14 text-center">
                      <div className="flex flex-col items-center">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                          <Inbox className="h-6 w-6" aria-hidden="true" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-slate-900">No notifications found</h3>
                        <p className="text-sm text-slate-500">Try adjusting your filters.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  notifications.map((notification, index) => {
                    // Calculate serial number based on current page and index
                    const serialNumber = pagination 
                      ? ((pagination.currentPage - 1) * pagination.limit) + index + 1
                      : index + 1;
                    
                    return (
                    <tr key={`${notification.type}-${notification.timestamp}-${index}`}>
                      <td className="whitespace-nowrap text-sm font-semibold text-slate-700">
                        {serialNumber}
                      </td>
                      <td className="whitespace-nowrap">
                        {formatNotificationType(notification.type)}
                      </td>
                      <td className="text-sm text-slate-900">
                        <div className="max-w-xs">
                          <p className="truncate font-semibold" title={notification.title}>
                            {notification.title}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-slate-500" title={notification.message}>
                            {notification.message}
                          </p>
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-sm text-slate-700">
                        {formatRecipient(notification)}
                      </td>
                      <td className="whitespace-nowrap text-sm text-slate-700">
                        {formatTimestamp(notification.timestamp)}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && pagination && pagination.totalPages > 1 && (
        <div className="admin-table-pagination">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              type="button"
              onClick={() => handlePageChange(Math.max(pagination.currentPage - 1, 1))}
              disabled={!pagination.hasPrevPage}
              className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => handlePageChange(Math.min(pagination.currentPage + 1, pagination.totalPages))}
              disabled={!pagination.hasNextPage}
              className="ml-3 inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-600">
                Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.limit) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}</span> of{' '}
                <span className="font-medium">{pagination.totalCount}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex -space-x-px overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" aria-label="Pagination">
                {/* Previous Button */}
                <button
                  type="button"
                  onClick={() => handlePageChange(Math.max(pagination.currentPage - 1, 1))}
                  disabled={!pagination.hasPrevPage}
                  className="relative inline-flex h-10 w-10 items-center justify-center border-r border-slate-200 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>

                {/* Page Numbers */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  // First page
                  if (startPage > 1) {
                    pages.push(
                      <button
                        type="button"
                        key={1}
                        onClick={() => handlePageChange(1)}
                        className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-medium text-slate-400">
                          ...
                        </span>
                      );
                    }
                  }

                  // Visible pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        type="button"
                        key={i}
                        onClick={() => handlePageChange(i)}
                        aria-current={i === pagination.currentPage ? 'page' : undefined}
                        className={`relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-semibold ${
                          i === pagination.currentPage
                            ? 'z-10 bg-slate-900 text-white'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Last page
                  if (endPage < pagination.totalPages) {
                    if (endPage < pagination.totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-medium text-slate-400">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        type="button"
                        key={pagination.totalPages}
                        onClick={() => handlePageChange(pagination.totalPages)}
                        className="relative inline-flex h-10 min-w-10 items-center justify-center border-r border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {pagination.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}

                {/* Next Button */}
                <button
                  type="button"
                  onClick={() => handlePageChange(Math.min(pagination.currentPage + 1, pagination.totalPages))}
                  disabled={!pagination.hasNextPage}
                  className="relative inline-flex h-10 w-10 items-center justify-center text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

    </div>
  );
});
